import {
    PRODUCT_CATEGORY,
    PRODUCT_CATEGORY_SCHEMA,
    PRODUCT_GROUP_TYPE,
    PRODUCT_TYPE_SCHEMA,
    PRODUCT_TYPES,
} from '@schuettflix/interfaces';
import { z } from 'zod';
import { productTypeToProductCategoryMap } from './constants/productTypeToProductCategoryMap.ts';

export const AvailableProductSchema = z.object({
    category: PRODUCT_CATEGORY_SCHEMA,
    productType: PRODUCT_TYPE_SCHEMA,
});

export function getCompatibleProductTypes({
    selectedProductGroupType,
    selectedProductTypes,
}: {
    selectedProductGroupType: PRODUCT_GROUP_TYPE;
    selectedProductTypes: PRODUCT_TYPES[];
}): {
    isValidSelection: boolean;
    compatibleProductTypes: PRODUCT_TYPES[];
} {
    const availableProductsGroupedByCategory = Object.entries(productTypeToProductCategoryMap)
        .filter(([productType]) => productType.includes('CUSTOM_'))
        .reduce<Record<PRODUCT_CATEGORY, PRODUCT_TYPES[]>>(
            (acc, [productType, productCategory]) => ({
                ...acc,
                [productCategory]: [...acc[productCategory], productType as PRODUCT_TYPES],
            }),
            {
                MATERIAL: [] as PRODUCT_TYPES[],
                TRANSPORT: [] as PRODUCT_TYPES[],
                MATERIAL_SERVICE: [] as PRODUCT_TYPES[],
                TRANSPORT_SERVICE: [] as PRODUCT_TYPES[],
            }
        );

    const hasMaterialSelected = selectedProductTypes.some(productType =>
        availableProductsGroupedByCategory.MATERIAL.includes(productType)
    );
    const hasTransportSelected = selectedProductTypes.some(productType =>
        availableProductsGroupedByCategory.TRANSPORT.includes(productType)
    );
    const hasTransportServiceSelected = selectedProductTypes.some(productType =>
        availableProductsGroupedByCategory.TRANSPORT_SERVICE.includes(productType)
    );
    const hasMaterialServiceSelected = selectedProductTypes.some(productType =>
        availableProductsGroupedByCategory.MATERIAL_SERVICE.includes(productType)
    );

    const hasOnlyServicesSelected = !hasMaterialSelected && !hasTransportSelected;

    if (['FRANCO', 'MERCHANT'].includes(selectedProductGroupType) || hasOnlyServicesSelected) {
        const selectedTransportCounts = selectedProductTypes.filter(productType =>
            availableProductsGroupedByCategory.TRANSPORT.includes(productType)
        ).length;
        const selectedMaterialCounts = selectedProductTypes.filter(productType =>
            availableProductsGroupedByCategory.MATERIAL.includes(productType)
        ).length;

        const isValidSelection = selectedTransportCounts <= 1 && selectedMaterialCounts <= 1;

        if (isValidSelection) {
            return {
                isValidSelection: true,
                compatibleProductTypes: [
                    ...availableProductsGroupedByCategory.MATERIAL_SERVICE,
                    ...availableProductsGroupedByCategory.TRANSPORT_SERVICE,
                ],
            };
        }

        return {
            isValidSelection: false,
            compatibleProductTypes: [],
        };
    }

    if (
        (hasMaterialSelected && !hasTransportSelected && hasTransportServiceSelected) ||
        (!hasMaterialSelected && hasTransportSelected && hasMaterialServiceSelected)
    ) {
        return {
            isValidSelection: false,
            compatibleProductTypes: [],
        };
    }

    return {
        isValidSelection: true,
        compatibleProductTypes: [
            ...selectedProductTypes,
            ...(hasMaterialSelected ? availableProductsGroupedByCategory.MATERIAL_SERVICE : []),
            ...(hasTransportSelected ? availableProductsGroupedByCategory.TRANSPORT_SERVICE : []),
        ],
    };
}
